/**
 * AP Simulation Library — central registry of every "AI-mocked" behaviour
 * across all Auto-Pilots.
 *
 * Why this exists
 * ---------------
 * The QMS POC fakes an LLM by hand-rolling intents, fixture signals, scripted
 * agent greetings, mock email replies, and chat-driven button presses. Today
 * those mocks are scattered across at least five files (backend simulator,
 * monitor instances, frontend ChatRail action handlers, qa-library tooltips,
 * exporter `simulateReply`). This file is the **single coordination point**
 * that lists every simulated behaviour per AP, with an honest `status` flag.
 *
 * Entries marked `'live'` already work — the canonical implementation lives
 * elsewhere (see `notes` for the file path); this registry only documents
 * them. Entries marked `'todo'` have no runtime yet — they read as a punch
 * list. `'partial'` means the primitive exists but isn't wired to its trigger.
 *
 * The shape is data-only on purpose. A future "simulation engine" can grow
 * here (matching messages, dispatching actions, scheduling timers) without
 * touching the per-AP component code. For now, components keep doing what
 * they do; this file gives reviewers and demo authors a map.
 *
 * Conventions
 * -----------
 * • User-facing strings are translation keys (QMS/CHAT_SIM/…) resolved by the
 *   engine via `translate()`. Keys live in `hooks/useTranslation/translations_*`.
 * • Code, comments, and `notes` are English.
 * • `[record.name]` style placeholders are resolved by the future engine —
 *   leave them literal in the data here.
 * • When in doubt about which AP "owns" a behaviour, file it under the AP
 *   whose ChatRail / cockpit surfaces it (not the backend that produces it).
 */

// ─── Types ─────────────────────────────────────────────────────────────────

/** Whether the registry entry is functionally wired today. */
export type SimStatus = "live" | "partial" | "todo"

/** Discriminated union of every action the simulated agent can dispatch. */
export type SimAction =
  // ── Existing (consumed by ChatRail today) ────────────────────────────────
  | { type: "open_session"; session_id: string } // diff session opens
  | { type: "accept_all" } // accepts all diffs
  | { type: "cancel_analysis" } // discards diff session

  // ── New, needed for the recording flow ───────────────────────────────────
  | { type: "open_record"; record_id: string } // selects a record + opens cockpit/bucket
  | { type: "create_email_draft"; record_id: string; template_ids?: string[] }
  | { type: "start_bucket_import"; record_id: string } // runs SupplierAktionenTab.handleImport
  | { type: "simulate_reply_after"; op_id: string; delay_ms: number }
  | { type: "navigate_to_ap"; ap_id: string } // landing → AP shell

/** Single chat-rail message with optional pacing. */
export interface SimMessage {
  text: string
  /** Stagger between this and the previous message, in ms. */
  delayMs?: number
}

/** Conditions under which an on-mount greeting fires. */
export type SimGreetingPredicate =
  | { kind: "always" }
  | { kind: "has_open_ausstehend" } // exporter has APWorkItems waiting
  | { kind: "has_pending_document" } // importer has docs queued
  | { kind: "has_fresh_customer_update" } // customer-conformity has new freshness
  | { kind: "has_open_finding" } // monitor has open befund

export interface SimGreeting {
  predicate: SimGreetingPredicate
  messages: SimMessage[]
  /** Optional follow-up: the agent listens for one of these patterns next. */
  followUp?: SimIntent
  status: SimStatus
  notes?: string
}

export interface SimIntent {
  /** Patterns matched against the user's normalised (trim/lowercase) message. */
  pattern: { regex: RegExp } | { exact: string[] }
  messages: SimMessage[]
  actions: SimAction[]
  status: SimStatus
  notes?: string
}

export interface SimTimer {
  /** Event in the AP's lifecycle that arms this timer. */
  trigger:
    | "after_email_send" // exporter: an email op was just created
    | "after_finding_apply" // monitor: a recommendation was applied
    | "after_bucket_create" // importer: a bucket appeared from forwardToImporter
  delayMs: number
  actions: SimAction[]
  status: SimStatus
  notes?: string
}

export interface SimWebhook {
  name: string
  description: string
  status: SimStatus
  notes?: string
}

export interface APSimulation {
  apId: string
  /** Hero title for inline UI/devtools labels — not shown to end users. */
  label: string
  greetings: SimGreeting[]
  intents: SimIntent[]
  timers: SimTimer[]
  webhooks: SimWebhook[]
}

// ─── Registry ──────────────────────────────────────────────────────────────

/**
 * Configuration AP — the only AP whose chat is fully wired today.
 *
 * Backend implementation: `backend/turnus_ai/qms_ingestion/simulator/chat.py`
 *   - INTENT_ACCEPT_ALL / INTENT_HELP / INTENT_CANCEL
 *   - `stream_document` — three-step narration paced to the AnalyzingProgress
 *     animation (intro → narrative → wrap-up + open_session action).
 */
const CONFIGURATION: APSimulation = {
  apId: "configuration",
  label: "Konfiguration",
  greetings: [],
  intents: [
    {
      pattern: { exact: ["alles richtig"] },
      messages: [
        { text: "QMS/CHAT_SIM/ACCEPT_ALL_CONFIRM" },
      ],
      actions: [{ type: "accept_all" }],
      status: "live",
      notes:
        "simulator/chat.py:_ACCEPT_ALL_TRIGGERS · ChatRail consumes accept_all action.",
    },
    {
      pattern: { exact: ["verwerfen", "abbrechen"] },
      messages: [{ text: "QMS/CHAT_SIM/CANCEL_CONFIRM" }],
      actions: [{ type: "cancel_analysis" }],
      status: "live",
      notes: "simulator/chat.py:_CANCEL_TRIGGERS.",
    },
    {
      pattern: {
        exact: [
          "hilfe",
          "help",
          "was kannst du",
          "was kannst du tun",
          "was kannst du für mich tun",
        ],
      },
      messages: [
        {
          text: "QMS/CHAT_SIM/HELP_RESPONSE",
        },
      ],
      actions: [],
      status: "live",
      notes: "simulator/chat.py:_HELP_TEXT.",
    },
  ],
  timers: [],
  webhooks: [
    {
      name: "document_upload",
      description:
        "Folder/file dropped into AktionenTab → bootstrap intent merges CSVs, " +
        "creates a session, streams a three-message analysis narrative, " +
        "and emits open_session.",
      status: "live",
      notes:
        "simulator/chat.py:stream_document; sessions/intents/bootstrap.py.",
    },
  ],
}

/**
 * Supplier (Lieferanten Deklarationen) — Importer.
 * Most of the Importer flow is button-driven today; chat is plumbed but only
 * returns the generic fallback. The script needs a proactive greeting after a
 * forwarded reply lands, plus a "Ja" intent to auto-press Importieren.
 */
const SUPPLIER: APSimulation = {
  apId: "supplier",
  label: "Lieferanten Deklarationen",
  greetings: [
    {
      predicate: { kind: "has_pending_document" },
      messages: [
        {
          text: "QMS/CHAT_SIM/SUPPLIER_GREETING",
        },
      ],
      followUp: {
        pattern: { regex: /^(ja|ja bitte|gerne|ok)\b/i },
        messages: [{ text: "QMS/CHAT_SIM/SUPPLIER_GREETING_CONFIRM" }],
        actions: [{ type: "start_bucket_import", record_id: "[record.id]" }],
        status: "live",
      },
      status: "live",
      notes:
        "Greeting triggers from useApSimulationEngine when api.pendingDocumentsList(supplier) " +
        "returns at least one doc with a target_record_id (typically created by forwardToImporter).",
    },
  ],
  intents: [],
  timers: [],
  webhooks: [
    {
      name: "forward_to_importer",
      description:
        'Exporter "An Lieferanten Deklaration übergeben" creates a PendingDocument ' +
        "targeting the supplier record; SupplierAktionenTab auto-selects the bucket.",
      status: "live",
      notes:
        "routers/exporter_operations.py + SupplierAktionenTab.tsx (pendingDocs effect).",
    },
    {
      name: "email_inbound",
      description:
        "Real Resend inbound webhook — drops a PendingDocument into the supplier AP.",
      status: "live",
      notes: "routers/resend_inbound.py.",
    },
  ],
}

/**
 * Customer-Conformity — Exporter.
 * Outputs a Kunden-Konformität PDF. The script wants a proactive greeting
 * announcing freshly-updated customers (Bosch in the demo).
 */
const CUSTOMER_CONFORMITY: APSimulation = {
  apId: "customer-conformity",
  label: "Kunden Konformitäten",
  greetings: [
    {
      predicate: { kind: "has_fresh_customer_update" },
      messages: [
        {
          text: "QMS/CHAT_SIM/CUSTOMER_GREETING",
        },
      ],
      followUp: {
        pattern: { regex: /^(ja|ja bitte|gerne|ok|los)\b/i },
        messages: [
          { text: "QMS/CHAT_SIM/CUSTOMER_GREETING_CONFIRM" },
        ],
        actions: [{ type: "open_record", record_id: "[record.id]" }],
        status: "live",
        notes:
          "Resolves to firstFreshCustomerId via the engine — opens the customer cockpit.",
      },
      status: "live",
      notes:
        "Predicate source: api.freshnessPartition fresh_record_ids " +
        "(promotion) OR stalest_relation_per_record demotion entries. " +
        "Greeting fires once per AP mount when either signal is non-empty.",
    },
  ],
  intents: [],
  timers: [],
  webhooks: [
    {
      name: "freshness_partition_update",
      description:
        "Triggered by Importer commits / monitor demotions → recomputes " +
        "fresh/stale partition and updates the customer-conformity card badge.",
      status: "live",
      notes: "AutoPilotsLanding.tsx polls api.freshnessPartition every 15s.",
    },
  ],
}

/**
 * Substanz Radar — Monitor (Demo A).
 * Fixture signal + recommendation generator already exist; chat is unwired.
 * Mirrors the Lieferanten-Tracking gaps below.
 */
const SUBSTANZ_RADAR: APSimulation = {
  apId: "substanz-radar",
  label: "Substanz Radar",
  greetings: [],
  intents: [
    {
      pattern: { regex: /svhc|kandidatenliste|reach|echa/i },
      messages: [
        { text: "QMS/CHAT_SIM/RADAR_CHECKING" },
        {
          text: "QMS/CHAT_SIM/RADAR_FOUND",
          delayMs: 1200,
        },
      ],
      actions: [],
      status: "todo",
      notes:
        "Backend handler missing: substanz-radar is not in routers/ap_chat.py:_KNOWN_AP_IDS. " +
        "Should run monitor evaluator and narrate the produced finding.",
    },
  ],
  timers: [],
  webhooks: [
    {
      name: "echa_signal",
      description:
        "Hard-coded fixture SVHC update → monitor evaluator produces finding.",
      status: "live",
      notes: "monitors/instances/substanz_radar.py:FIXTURE_SIGNAL.",
    },
  ],
}

/**
 * Lieferanten Tracking — Monitor (Demo B).
 * Same shape as substanz-radar. The recording calls for a chat-prompt-driven
 * finding ("Welcher meiner Zulieferer ist vom letzten SVHC-Update betroffen?").
 */
const LIEFERANTEN_TRACKING: APSimulation = {
  apId: "lieferanten-tracking",
  label: "Lieferanten Tracking",
  greetings: [],
  intents: [
    {
      pattern: { regex: /(svhc|shvc|kandidatenliste|reach|echa|update)/i },
      messages: [
        { text: "QMS/CHAT_SIM/RADAR_CHECKING" },
        {
          text: "QMS/CHAT_SIM/TRACKING_FOUND",
          delayMs: 1500,
        },
      ],
      actions: [],
      status: "live",
      notes:
        "Engine intercepts on the frontend (no backend chat handler), then calls " +
        'api.monitorEvaluate("lieferanten-tracking") to actually create the finding ' +
        "so the Befunde list updates after the canned narration.",
    },
  ],
  timers: [],
  webhooks: [
    {
      name: "svhc_signal",
      description:
        "Hard-coded fixture SVHC update → evaluator produces finding for Aurubis AG.",
      status: "live",
      notes: "monitors/instances/lieferanten_tracking.py:FIXTURE_SIGNAL.",
    },
    {
      name: "manual_evaluate_button",
      description: '„Jetzt prüfen" button → POST /monitor/{ap_id}/evaluate.',
      status: "live",
      notes: "LieferantenTrackingAktionenTab.tsx:handleEvaluate.",
    },
  ],
}

/**
 * Lieferanten Abfragen — Exporter (Demo B).
 * Greeting after a befund recommendation lands, "Ja" → drafted email in the
 * cockpit timeline. The waiting → answered_received transition is driven by
 * the manual "Antwort simulieren" button in the cockpit (same UX as
 * customer-conformity) — no after_email_send timer.
 *
 * Note: the `'draft'` ExportOpState does not exist yet (exportOperationsStore.ts:25).
 * `create_email_draft` therefore depends on adding it to the state union.
 */
const LIEFERANTEN_ABFRAGEN: APSimulation = {
  apId: "lieferanten-abfragen",
  label: "Lieferanten Abfragen",
  greetings: [
    {
      predicate: { kind: "has_open_ausstehend" },
      messages: [
        {
          text: "QMS/CHAT_SIM/QUERIES_GREETING",
        },
      ],
      followUp: {
        pattern: { regex: /^(ja|gerne|ok|los|aurubis)\b/i },
        messages: [
          {
            text: "QMS/CHAT_SIM/QUERIES_GREETING_CONFIRM",
          },
        ],
        actions: [
          { type: "open_record", record_id: "[record.id]" },
          { type: "create_email_draft", record_id: "[record.id]" },
        ],
        status: "live",
        notes:
          "Pushes a draft op into exportOperationsStore (frontend-only, channel=email, state=draft).",
      },
      status: "live",
      notes:
        "Predicate source: api.apPartition.ausstehend.length > 0. " +
        "No backend chat handler needed — engine intercepts on the frontend.",
    },
  ],
  intents: [],
  timers: [],
  webhooks: [
    {
      name: "queue_export_entries",
      description:
        "Monitor recommendation applied → IntegrationQueueItem row created on this AP. " +
        "Drives the AP-tile notification badge and the Ausstehend section.",
      status: "live",
      notes:
        'routers/inbox.py:("lieferanten-abfragen","queue_export_entries").',
    },
    {
      name: "resend_inbound",
      description:
        "Real inbound email reply matches by provider_message_id → flips op state " +
        "to answered_received with attachment.",
      status: "live",
      notes: "routers/resend_inbound.py + simulateReply mock equivalent.",
    },
  ],
}

// ─── Public registry + helpers ─────────────────────────────────────────────

export const AP_SIMULATIONS: Record<string, APSimulation> = {
  configuration: CONFIGURATION,
  supplier: SUPPLIER,
  "customer-conformity": CUSTOMER_CONFORMITY,
  "substanz-radar": SUBSTANZ_RADAR,
  "lieferanten-tracking": LIEFERANTEN_TRACKING,
  "lieferanten-abfragen": LIEFERANTEN_ABFRAGEN,
}

export function getApSimulation(apId: string): APSimulation | undefined {
  return AP_SIMULATIONS[apId]
}

/** Counts entries by status across the registry — useful for dashboards/tests. */
export function summariseSimStatus(): Record<SimStatus, number> {
  const out: Record<SimStatus, number> = { live: 0, partial: 0, todo: 0 }
  for (const sim of Object.values(AP_SIMULATIONS)) {
    for (const g of sim.greetings) {
      out[g.status]++
      if (g.followUp) out[g.followUp.status]++
    }
    for (const i of sim.intents) out[i.status]++
    for (const t of sim.timers) out[t.status]++
    for (const w of sim.webhooks) out[w.status]++
  }
  return out
}
